/**
 * Export text (CSV/SVG): native share sheet on iOS/Android, download on web.
 */
import { Capacitor } from '@capacitor/core';

export async function shareText(text: string, filename: string, mime: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem');
      const { Share } = await import('@capacitor/share');
      const res = await Filesystem.writeFile({ path: filename, data: text, directory: Directory.Cache, encoding: Encoding.UTF8 });
      await Share.share({ title: filename, url: res.uri });
      return;
    } catch (e) {
      console.warn('[share] native share failed, falling back', e);
    }
  }
  const blob = new Blob([text], { type: mime });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

/**
 * Share a file (image, PDF …): the phone's share sheet — Messages, Mail,
 * AirDrop, WhatsApp … — in the app and in phone browsers that support
 * sharing files; a download everywhere else. Returns how it went so the
 * caller can say "Saved to downloads" instead.
 */
export async function shareFile(blob: Blob, filename: string, title?: string): Promise<'shared' | 'cancelled' | 'downloaded'> {
  if (!Capacitor.isNativePlatform() && typeof navigator !== 'undefined' && navigator.canShare) {
    const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
    if (navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: title ?? filename });
        return 'shared';
      } catch (e) {
        if ((e as Error).name === 'AbortError') return 'cancelled';
        console.warn('[share] web share failed, falling back', e);
      }
    }
  }
  if (Capacitor.isNativePlatform()) {
    await shareBlob(blob, filename);
    return 'shared';
  }
  await shareBlob(blob, filename);
  return 'downloaded';
}

/** "Insurance card — Unit 104" + ".pdf" → a safe file name. */
export function fileNameFor(title: string, path: string): string {
  const ext = (path.match(/\.([a-z0-9]{2,4})$/i)?.[1] ?? 'jpg').toLowerCase();
  const base = title.replace(/[—–]/g, '-').replace(/[^\w\s.-]/g, '').replace(/\s+/g, ' ').trim() || 'document';
  return `${base}.${ext}`;
}

export async function shareBlob(blob: Blob, filename: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      const { Share } = await import('@capacitor/share');
      const b64 = await new Promise<string>((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(',')[1] ?? '');
        r.onerror = rej;
        r.readAsDataURL(blob);
      });
      const f = await Filesystem.writeFile({ path: filename, data: b64, directory: Directory.Cache });
      await Share.share({ title: filename, url: f.uri });
      return;
    } catch (e) {
      console.warn('[share] native share failed, falling back', e);
    }
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
